import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Checkbox,
  FormControlLabel,
  Tab,
  Tabs,
  TextField,
  Typography,
  Autocomplete
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Delete,
  Person,
  BusinessCenter,
  AttachFile,
  Security,
} from '@mui/icons-material';
import { ROUTES } from '../../../routes';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../Api/endpoints';
import { addAuthHeader } from '../../../utils/authUtils';
import apiService from '../../../services/api';
  
export const EMPLOYEE_STATUS_OPTIONS = [
  { label: "Active", value: "ACTIVE" },
  { label: "Deactivated", value: "DEACTIVATED" },
  { label: "Absconded", value: "ABSCONDED" },
  { label: "Long Leave", value: "LONG_LEAVE" }
]

// No mock data. All data fetched from backend.

// Custom TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`employee-tabpanel-${index}`}
      aria-labelledby={`employee-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function EmployeeEdit({ mode = 'employee' }) {
  const isStakeholder = mode === 'stakeholder';
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [employee, setEmployee] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    nationalId: '',
    passportNumber: '',
    passportExpiry: '',
    passportIssue: '',
    emiratesIdIssueDate: '',
    emiratesIdExpiryDate: '',
    visaNumber: '',
    visaType: '',
    visaIssueDate: '',
    visaExpiryDate: '',
    visaSponsorName: '',
    nationality: '',
    profession: null,
    professionId: null,
    requireOhc: false,
    requireDrivingLicense: false,
    address: '',
    poBox: '',
    department: null, // Will store full object {id, departmentName}
    position: null,   // Will store full object {id, positionName}
    startDate: '',
    employeeId: '',
    reportsTo: '',
    contractType: 'Full-time',
    status: 'ACTIVE',
    statusInfo: '',
    employeeRole: '',
    ownershipPercentage: '',
    username: '',
    password: '',
    accessLevel: 'CUSTOMER_EMPLOYEE',
    loginEnabled: false,
  });
  const [errors, setErrors] = useState({});
  
  // Profession search states
  const [professionSearchResults, setProfessionSearchResults] = useState([]);
  const [professionSearchLoading, setProfessionSearchLoading] = useState(false);
  const [professionInputValue, setProfessionInputValue] = useState('');
  const professionSearchTimeoutRef = useRef(null);

  // Fetch departments and positions on component mount
  useEffect(() => {
    const fetchDepartmentsAndPositions = async () => {
      try {
        const response = await apiService.get('/v1/departments-positions');
        setDepartments(response.departments || []);
        setPositions(response.positions || []);
      } catch (error) {
        console.error('Error fetching departments and positions:', error);
      }
    };
    
    fetchDepartmentsAndPositions();
  }, []);

  // Fetch employee data
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        setLoading(true);
        const resp = await axios.get(API_ENDPOINTS.EMPLOYEES.GET(id), { headers: addAuthHeader() });
        const data = resp?.data?.data || resp?.data || {};
        setEmployee(data);
        
        // Helper function to format date for input field (YYYY-MM-DD)
        const formatDateForInput = (dateValue) => {
          if (!dateValue) return '';
          try {
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) return '';
            return date.toISOString().split('T')[0];
          } catch {
            return '';
          }
        };
        
        setFormData({
          firstName: data.firstName || '',
          middleName: data.middleName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          phone: data.phone || data.mobileNumber || data.mobile || '',
          dateOfBirth: formatDateForInput(data.dateOfBirth || data.dob),
          gender: data.gender || '',
          nationalId: data.nationalId || data.emiratesId || '',
          passportNumber: data.passportNumber || '',
          passportExpiry: formatDateForInput(data.passportExpiry),
          passportIssue: data.passportIssue || '',
          emiratesIdIssueDate: formatDateForInput(data.emiratesIdIssueDate),
          emiratesIdExpiryDate: formatDateForInput(data.emiratesIdExpiryDate),
          visaNumber: data.visaNumber || '',
          visaType: data.visaType || '',
          visaIssueDate: formatDateForInput(data.visaIssueDate),
          visaExpiryDate: formatDateForInput(data.visaExpiryDate),
          visaSponsorName: data.visaSponsorName || '',
          nationality: data.nationality || '',
          profession: data.profession?.title || null,
          professionId: data.profession?.id || null,
          requireOhc: data.requireOhc || false,
          requireDrivingLicense: data.requireDrivingLicense || false,
          address: data.address || '',
          poBox: data.poBox || '',
          // Store full objects if available
          department: data.department || null,
          position: data.position || null,
          startDate: formatDateForInput(data.startDate),
          employeeId: data.employeeId || '',
          reportsTo: data.reportsTo || '',
          contractType: data.contractType || 'Full-time',
          status: data.status || 'ACTIVE',
          statusInfo: data.statusInfo || '',
          employeeRole: data.employeeRole || '',
          ownershipPercentage: data.ownershipPercentage || '',
          username: data.username || '',
          password: '', // Never populate password from response
          accessLevel: data.accessLevel || 'CUSTOMER_EMPLOYEE',
          loginEnabled: !!(data && data.username),
        });
      } catch (err) {
        console.error('Failed to fetch employee details:', err);
        setError('Failed to load employee details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeData();
  }, [id]);

  // Initialize profession input value when profession is loaded
  useEffect(() => {
    if (formData.profession) {
      setProfessionInputValue(formData.profession);
    }
  }, [formData.profession]);

  // Handle form field changes
  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData({
      ...formData,
      [field]: value
    });
    
    // Clear error for this field when changed
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: null
      });
    }
  };

  // Toggle login enable/disable (component scope)
  const handleToggleLogin = (e) => {
    const checked = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      loginEnabled: checked,
      // Optionally clear username when disabling
      username: checked ? prev.username : '',
    }));
    // Clear username error when disabling login
    if (!checked && errors.username) {
      setErrors((prev) => ({ ...prev, username: null }));
    }
  };

  // Search professions with debouncing (minimum 2 characters)
  const searchProfessions = async (searchQuery) => {
    // Clear previous timeout
    if (professionSearchTimeoutRef.current) {
      clearTimeout(professionSearchTimeoutRef.current);
    }

    // Don't search if less than 2 characters
    if (!searchQuery || searchQuery.trim().length < 2) {
      setProfessionSearchResults([]);
      setProfessionSearchLoading(false);
      return;
    }

    // Set loading state
    setProfessionSearchLoading(true);

    // Debounce the API call
    professionSearchTimeoutRef.current = setTimeout(async () => {
      try {
        console.log('Searching professions:', searchQuery);
        const response = await apiService.get('/v1/professions/search', {
          query: searchQuery.trim()
        });

        // Handle response - expecting array of professions
        if (Array.isArray(response)) {
          setProfessionSearchResults(response);
        } else if (response && Array.isArray(response.data)) {
          setProfessionSearchResults(response.data);
        } else {
          setProfessionSearchResults([]);
        }
      } catch (error) {
        console.error('Error searching professions:', error);
        setProfessionSearchResults([]);
      } finally {
        setProfessionSearchLoading(false);
      }
    }, 300); // 300ms debounce delay
  };

  // Fetch profession details and update govt requirements
  const fetchProfessionDetails = async (professionId) => {
    if (!professionId) {
      console.log('No profession ID to fetch details');
      return;
    }
    
    try {
      console.log('Fetching profession details for ID:', professionId);
      const response = await apiService.get(`/v1/professions/${professionId}`);
      
      if (response) {
        console.log('Profession details fetched:', response);
        
        // Update form data with govt requirements
        const govtReqs = response.govtRequirements || {};
        setFormData(prev => ({
          ...prev,
          requireOhc: govtReqs.ohc === true,
          requireDrivingLicense: govtReqs.drivingLicense === true
        }));
        
        console.log('Updated govt requirements:', {
          requireOhc: govtReqs.ohc === true,
          requireDrivingLicense: govtReqs.drivingLicense === true
        });
      }
    } catch (error) {
      console.error('Error fetching profession details:', error);
      // Don't show error to user, just log it
      // The fields will remain at their current values
    }
  };

  // Handle profession selection from autocomplete
  const handleProfessionSelect = async (event, newValue) => {
    if (newValue) {
      setFormData(prev => ({
        ...prev,
        profession: newValue.title,
        professionId: newValue.id
      }));
      
      console.log('Profession selected:', newValue);
      
      // fetch details when a profession is selected

      await fetchProfessionDetails(newValue.id);

    } else {
      // Clear selection
      setFormData(prev => ({
        ...prev,
        profession: null,
        professionId: null,
        requireOhc: false,
        requireDrivingLicense: false
      }));
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // No client-side filtering; department/position are free-text in this phase.

  // Validate current tab
  const validateTab = (tab) => {
    const tabErrors = {};
    
    if (tab === 0) {
      // Validate Personal Information
      if (!formData.firstName.trim()) {
        tabErrors.firstName = 'First name is required';
      }
      
      if (!formData.lastName.trim()) {
        tabErrors.lastName = 'Last name is required';
      }
      
      if (!formData.phone.trim()) {
        tabErrors.phone = 'Phone number is required';
      }
      
      if (!formData.nationalId.trim() && !formData.passportNumber.trim()) {
        tabErrors.nationalId = 'Either National ID or Passport Number is required';
        tabErrors.passportNumber = 'Either National ID or Passport Number is required';
      }
    } 
    else if (tab === 1) {
      if (isStakeholder) {
        // Validate Stakeholder Details
        if (!formData.employeeRole) {
          tabErrors.employeeRole = 'Stakeholder role is required';
        }
      } else {
        // Validate Job Details
        if (!formData.department) {
          tabErrors.department = 'Department is required';
        }

        if (!formData.position) {
          tabErrors.position = 'Position is required';
        }

        if (!formData.startDate) {
          tabErrors.startDate = 'Start date is required';
        }
      }
    }
    else if (tab === 2) {
      // Validate Access Information
      // Email now validated here
      if (!formData.email.trim()) {
        tabErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        tabErrors.email = 'Please enter a valid email address';
      }
      // Username required only if login is enabled
      if (formData.loginEnabled) {
        if (!formData.username.trim()) {
          tabErrors.username = 'Username is required';
        } else if (formData.username.length < 4) {
          tabErrors.username = 'Username must be at least 4 characters';
        }
      }
    }
    
    return tabErrors;
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate all tabs
    const personalErrors = validateTab(0);
    const jobErrors = validateTab(1);
    const accessErrors = validateTab(2);
    
    const allErrors = {
      ...personalErrors,
      ...jobErrors,
      ...accessErrors
    };
    
    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      
      // Switch to the first tab with errors
      if (Object.keys(personalErrors).length > 0) {
        setTabValue(0);
      } else if (Object.keys(jobErrors).length > 0) {
        setTabValue(1);
      } else {
        setTabValue(2);
      }
      
      return;
    }
    
    setSaving(true);
    
    try {
      // Build payload according to EmployeeCreationRequest structure
      const payload = {
        personalInfo: {
          firstName: formData.firstName,
          middleName: formData.middleName || '',
          lastName: formData.lastName,
          mobileNumber: formData.phone,
          dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth).toISOString() : null,
          gender: formData.gender || null, // Add gender field if needed in formData
          passportNumber: formData.passportNumber || '',
          passportExpiry: formData.passportExpiry ? new Date(formData.passportExpiry).toISOString() : null,
          passportIssue: formData.passportIssue || null,
          emiratesId: formData.nationalId || '',
          emiratesIdIssueDate: formData.emiratesIdIssueDate ? new Date(formData.emiratesIdIssueDate).toISOString() : null,
          emiratesIdExpiryDate: formData.emiratesIdExpiryDate ? new Date(formData.emiratesIdExpiryDate).toISOString() : null,
          visaNumber: formData.visaNumber || null,
          visaType: formData.visaType || null,
          visaIssueDate: formData.visaIssueDate ? new Date(formData.visaIssueDate).toISOString() : null,
          visaExpiryDate: formData.visaExpiryDate ? new Date(formData.visaExpiryDate).toISOString() : null,
          visaSponsorName: formData.visaSponsorName || null,
          professionId: formData.professionId || null,
          requireOhc: formData.requireOhc || false,
          requireDrivingLicense: formData.requireDrivingLicense || false,
          nationality: formData.nationality || null,
        },
        ...(isStakeholder ? {} : {
          employmentInfo: {
            department: formData.department ? {
              id: formData.department.id,
              departmentName: formData.department.departmentName
            } : null,
            position: formData.position ? {
              id: formData.position.id,
              positionName: formData.position.positionName
            } : null,
            startDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
            employeeId: formData.employeeId || null,
            reportsTo: formData.reportsTo || null,
            contractType: formData.contractType || 'Full-time',
            status: formData.status || 'ACTIVE',
            statusInfo: formData.statusInfo || null,
          },
        }),
        employeeRole: formData.employeeRole || null,
        ownershipPercentage: formData.ownershipPercentage || null,
        contactInfo: {
          email: formData.email,
          address: formData.address || '',
          poBox: formData.poBox || null
        },
        requiresLogin: formData.loginEnabled,
        mobileNumber: formData.phone, // Top-level fallback for backward compatibility
        dateOfBirth: formData.dateOfBirth || null, // Top-level fallback
        documents: []
      };

      // Add userAccess only if login is enabled
      if (formData.loginEnabled && formData.username) {
        payload.userAccess = {
          username: formData.username,
          password: formData.password || '', // Password may not be required for update
          accessLevel: formData.accessLevel || 'CUSTOMER_EMPLOYEE'
        };
      }

      await axios.put(API_ENDPOINTS.EMPLOYEES.UPDATE(id), payload, { headers: addAuthHeader({ 'Content-Type': 'application/json' }) });

      // Navigate to the detail view
      const detailRoute = isStakeholder ? ROUTES.CUSTOMER.STAKEHOLDERS.DETAIL(id) : ROUTES.CUSTOMER.EMPLOYEES.DETAIL(id);
      navigate(detailRoute);
    } catch (error) {
      console.error('Error updating employee:', error);
      setError('Failed to update employee. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  const editRoutes = isStakeholder ? ROUTES.CUSTOMER.STAKEHOLDERS : ROUTES.CUSTOMER.EMPLOYEES;

  const handleCancel = () => {
    navigate(editRoutes.DETAIL(id));
  };

  const handleBackToList = () => {
    navigate(editRoutes.LIST);
  };

  // Navigate to documents management
  const handleManageDocuments = () => {
    navigate(ROUTES.CUSTOMER.EMPLOYEES.DOCUMENTS(id));
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 5, mb: 5, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading employee data...
        </Typography>
      </Container>
    );
  }

  if (error && !employee) {
    return (
      <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
        <Alert severity="error">{error}</Alert>
        <Box mt={3} textAlign="center">
          <Button 
            variant="contained" 
            onClick={handleBackToList}
            startIcon={<ArrowBack />}
          >
            Back to Employee List
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 3, mb: 5 }}>
      <Box mb={3} display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <IconButton onClick={handleCancel} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" component="h1">
            {isStakeholder ? 'Edit Stakeholder' : 'Edit Employee'}
          </Typography>
        </Box>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<AttachFile />}
            sx={{ mr: 1 }}
            onClick={handleManageDocuments}
            disabled={saving}
          >
            Manage Documents
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="employee tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Personal Information" icon={<Person />} iconPosition="start" />
            <Tab label={isStakeholder ? "Stakeholder Details" : "Job Details"} icon={<BusinessCenter />} iconPosition="start" />
            <Tab label="Account Access" icon={<Security />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Personal Information Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3} sx={{ p: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange('firstName')}
                error={!!errors.firstName}
                helperText={errors.firstName || ''}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Middle Name"
                name="middleName"
                value={formData.middleName}
                onChange={handleChange('middleName')}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange('lastName')}
                error={!!errors.lastName}
                helperText={errors.lastName || ''}
                required
              />
            </Grid>

            {/* Email moved to Account Access tab */}
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange('phone')}
                error={!!errors.phone}
                helperText={errors.phone || ''}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                name="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={handleChange('dateOfBirth')}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="National ID"
                name="nationalId"
                value={formData.nationalId}
                onChange={handleChange('nationalId')}
                error={!!errors.nationalId}
                helperText={errors.nationalId || ''}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Passport Number"
                name="passportNumber"
                value={formData.passportNumber}
                onChange={handleChange('passportNumber')}
                error={!!errors.passportNumber}
                helperText={errors.passportNumber || ''}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={professionSearchResults}
                loading={professionSearchLoading}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.title || '';
                }}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  return option.id === value.id;
                }}
                value={formData.professionId && formData.profession ? { id: formData.professionId, title: formData.profession } : null}
                onChange={handleProfessionSelect}
                inputValue={professionInputValue}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'reset') {
                    setProfessionInputValue(newInputValue);
                    return;
                  }
                  setProfessionInputValue(newInputValue);
                  searchProfessions(newInputValue);
                }}
                onBlur={() => {
                  // If user typed but didn't select (no professionId), clear the input
                  if (!formData.professionId && professionInputValue) {
                    setProfessionInputValue('');
                    setProfessionSearchResults([]);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Profession"
                    placeholder="Type at least 2 characters to search"
                    helperText={
                      professionInputValue && professionInputValue.length < 2
                        ? 'Type at least 2 characters to search'
                        : ''
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {professionSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2">{option.title}</Typography>
                      {option.description && (
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                noOptionsText={
                  professionInputValue && professionInputValue.length < 2
                    ? "Type at least 2 characters"
                    : "No professions found"
                }
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nationality"
                name="nationality"
                value={formData.nationality}
                onChange={handleChange('nationality')}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.requireOhc}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, requireOhc: e.target.checked }));
                    }}
                    name="requireOhc"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    OHC Required
                  </Typography>
                }
                sx={{ mt: 1 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.requireDrivingLicense}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, requireDrivingLicense: e.target.checked }));
                    }}
                    name="requireDrivingLicense"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    Driving License Required
                  </Typography>
                }
                sx={{ mt: 1 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                multiline
                rows={3}
                value={formData.address}
                onChange={handleChange('address')}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Job Details / Stakeholder Details Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3} sx={{ p: 3 }}>
            {isStakeholder ? (
              <>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required error={!!errors.employeeRole}>
                    <InputLabel>Stakeholder Role</InputLabel>
                    <Select
                      name="employeeRole"
                      value={formData.employeeRole}
                      onChange={handleChange('employeeRole')}
                      label="Stakeholder Role"
                    >
                      <MenuItem value="OWNER">Owner</MenuItem>
                      <MenuItem value="PARTNER">Partner</MenuItem>
                      <MenuItem value="DIRECTOR">Director</MenuItem>
                    </Select>
                    {errors.employeeRole && <FormHelperText>{errors.employeeRole}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Ownership Percentage"
                    name="ownershipPercentage"
                    value={formData.ownershipPercentage}
                    onChange={handleChange('ownershipPercentage')}
                    placeholder="e.g. 25%"
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required error={!!errors.department}>
                    <InputLabel>Department</InputLabel>
                    <Select
                      name="department"
                      value={formData.department ? JSON.stringify(formData.department) : ''}
                      onChange={(e) => {
                        const dept = e.target.value ? JSON.parse(e.target.value) : null;
                        setFormData(prev => ({ ...prev, department: dept }));
                        if (errors.department) {
                          setErrors({ ...errors, department: null });
                        }
                      }}
                      label="Department"
                    >
                      {departments.map(dept => (
                        <MenuItem key={dept.id} value={JSON.stringify(dept)}>
                          {dept.departmentName}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.department && <FormHelperText>{errors.department}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required error={!!errors.position}>
                    <InputLabel>Position</InputLabel>
                    <Select
                      name="position"
                      value={formData.position ? JSON.stringify(formData.position) : ''}
                      onChange={(e) => {
                        const pos = e.target.value ? JSON.parse(e.target.value) : null;
                        setFormData(prev => ({ ...prev, position: pos }));
                        if (errors.position) {
                          setErrors({ ...errors, position: null });
                        }
                      }}
                      label="Position"
                    >
                      {positions.map(pos => (
                        <MenuItem key={pos.id} value={JSON.stringify(pos)}>
                          {pos.positionName}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.position && <FormHelperText>{errors.position}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    name="startDate"
                    type="date"
                    value={formData.startDate}
                    onChange={handleChange('startDate')}
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.startDate}
                    helperText={errors.startDate || ''}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Employee ID"
                    name="employeeId"
                    value={formData.employeeId}
                    onChange={handleChange('employeeId')}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Reports To"
                    name="reportsTo"
                    value={formData.reportsTo}
                    onChange={handleChange('reportsTo')}
                    helperText="Manager or supervisor name"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="contract-label">Contract Type</InputLabel>
                    <Select
                      labelId="contract-label"
                      label="Contract Type"
                      value={formData.contractType}
                      onChange={handleChange('contractType')}
                    >
                      <MenuItem value="Full-time">Full-time</MenuItem>
                      <MenuItem value="Part-time">Part-time</MenuItem>
                      <MenuItem value="Contract">Contract</MenuItem>
                      <MenuItem value="Temporary">Temporary</MenuItem>
                      <MenuItem value="Intern">Intern</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="status-label">Status</InputLabel>
                    <Select
                      labelId="status-label"
                      label="Status"
                      value={formData.status}
                      onChange={handleChange('status')}
                    >
                      {EMPLOYEE_STATUS_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Status Info"
                    name="statusInfo"
                    value={formData.statusInfo}
                    onChange={handleChange('statusInfo')}
                    helperText="status change summary"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </TabPanel>

        {/* Account Access Tab */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3} sx={{ p: 3 }}>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={!!formData.loginEnabled} onChange={handleToggleLogin} />}
                label="Enable login access for this employee"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                error={!!errors.email}
                helperText={errors.email || ''}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange('username')}
                error={!!errors.username}
                helperText={errors.username || (formData.loginEnabled ? 'Employee will use this to log in' : 'Login is disabled. Enable to set username.')}
                required={formData.loginEnabled}
                disabled={!formData.loginEnabled}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="access-level-label">Access Level</InputLabel>
                <Select
                  labelId="access-level-label"
                  label="Access Level"
                  value={formData.accessLevel}
                  onChange={handleChange('accessLevel')}
                  disabled // In this phase, we only allow CUSTOMER_EMPLOYEE
                >
                  <MenuItem value="CUSTOMER_EMPLOYEE">Employee (Standard Access)</MenuItem>
                  <MenuItem value="CUSTOMER_ADMIN">Admin (Advanced Access)</MenuItem>
                </Select>
                <FormHelperText>
                  Standard level grants basic access to employee self-service features
                </FormHelperText>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Password Management
              </Typography>
              <Button 
                variant="outlined" 
                color="primary"
                sx={{ mr: 2 }}
              >
                Reset Password
              </Button>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                Note: Password reset is simulated in Phase 1 UI and won't affect any real data.
              </Typography>
            </Grid>
          </Grid>
        </TabPanel>

        <Divider />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Card>
    </Container>
  );
}

export default EmployeeEdit;
